import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchCuisines, deleteCuisine, updateCuisineOrders, searchCuisines, bulkDeleteCuisines, bulkUpdateCuisineStatus } from "./cuisineSlice";
import Swal from 'sweetalert2';
import ConfirmationModal from "../../components/ConfirmationModal";
import Loader from "../../components/Loader";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import log from "../../logger";
import DndTable from "../../components/DndTable";
import LoadingSpinner from "../../components/LoadingSpinner";
import { paths } from "../../routes/helpers";
import { getImageUrl } from "../../utils/urlUtils";
import useDebounce from "../../hooks/useDebounce";

const CuisineList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, loading, error, total } = useSelector(
    (state) => state.cuisine
  );

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCuisine, setSelectedCuisine] = useState(null);
  const [displayLimit, setDisplayLimit] = useState(10);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 1000);

  useEffect(() => {
    if (debouncedSearchTerm) {
      dispatch(searchCuisines({ keyword: debouncedSearchTerm, page: 1, limit: displayLimit }));
    } else {
      dispatch(fetchCuisines({ page: 1, limit: displayLimit }));
    }
  }, [dispatch, displayLimit, debouncedSearchTerm]);

  const handleDelete = (cuisineId) => {
    setShowDeleteModal(true);
    setSelectedCuisine(cuisineId);
  };

  const confirmDelete = async () => {
    if (selectedCuisine) {
      try {
        await dispatch(deleteCuisine(selectedCuisine)).unwrap();
        toast.success("Cuisine deleted successfully!");
        log.info("Cuisine deleted:", selectedCuisine);
      } catch (err) {
        toast.error(err || "Failed to delete cuisine");
        log.error("Error deleting cuisine:", err);
      } finally {
        setShowDeleteModal(false);
        setSelectedCuisine(null);
      }
    }
  };

  const handleEdit = (cuisineId) => {
    navigate(paths.private.cuisine.edit(cuisineId));
  };

  const handleCreateCuisine = () => {
    navigate(paths.private.cuisine.create());
  };

  const handleLoadMore = () => {
    const newLimit = displayLimit + 10;
    setDisplayLimit(newLimit);
    
    // Check if we've loaded all items
    if (newLimit >= total) {
      setHasMore(false);
    }
  };

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
    setDisplayLimit(10); // Reset to initial limit when searching
    setHasMore(true);
  };

  const handleBulkDelete = async (ids, clearSelection) => {
    const result = await Swal.fire({
      title: `Delete ${ids.length} cuisine${ids.length > 1 ? 's' : ''}?`,
      text: 'This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    try {
      await dispatch(bulkDeleteCuisines(ids)).unwrap();
      Swal.fire({ title: 'Deleted!', text: `${ids.length} cuisine${ids.length > 1 ? 's' : ''} deleted.`, icon: 'success', timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ title: 'Some deletions failed', text: err || 'One or more items are still in use elsewhere and were not deleted.', icon: 'error' });
    } finally {
      clearSelection();
      if (debouncedSearchTerm) {
        dispatch(searchCuisines({ keyword: debouncedSearchTerm, page: 1, limit: displayLimit }));
      } else {
        dispatch(fetchCuisines({ page: 1, limit: displayLimit }));
      }
    }
  };

  const handleBulkStatusChange = (status) => async (ids, clearSelection) => {
    const activating = status === 1;
    const result = await Swal.fire({
      title: `${activating ? 'Activate' : 'Deactivate'} ${ids.length} cuisine${ids.length > 1 ? 's' : ''}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: `Yes, ${activating ? 'activate' : 'deactivate'}`,
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    dispatch(bulkUpdateCuisineStatus({ ids, status })).then(() => {
      Swal.fire({ title: 'Updated!', text: `${ids.length} cuisine${ids.length > 1 ? 's' : ''} ${activating ? 'activated' : 'deactivated'}.`, icon: 'success', timer: 1500, showConfirmButton: false });
      clearSelection();
    });
  };

  const getRemainingItemsCount = () => {
    return total - Math.min(displayLimit, items.length);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
  
    if (active.id !== over.id) {
      const oldIndex = items.findIndex(item => item._id === active.id);
      const newIndex = items.findIndex(item => item._id === over.id);
  
      // Step 1: Make a copy and update positions
      const reorderedItems = [...items];
      const [movedItem] = reorderedItems.splice(oldIndex, 1);
      reorderedItems.splice(newIndex, 0, movedItem);
  
      // Step 2: Assign new order to each item (1-based index preferred)
      const updatedCuisines = reorderedItems.map((item, index) => ({
        id: item._id,
        order: index + 1
      }));
  
      try {
        // Step 3: Optimistically update UI
        dispatch({ type: 'cuisine/reorderCuisines', payload: { oldIndex, newIndex } });
  
        // Step 4: Send updated orders to backend
        await dispatch(updateCuisineOrders(updatedCuisines)).unwrap();
        toast.success('Cuisine order updated successfully!');
      } catch (error) {
        // Step 5: Restore original state on failure
        dispatch(fetchCuisines({ page: 1, limit: displayLimit }));
        toast.error('Failed to update cuisine order');
        log.error('Error updating cuisine order:', error);
      }
    }
  };

  const formatTableData = (cuisines) => {
    return cuisines.map((cuisine) => ({
      _id: cuisine._id,
      label: cuisine.label || '-',
      image: getImageUrl(cuisine.image) || null,
      status: cuisine.status ? 'Active' : 'Inactive',
      createdAt: cuisine.createdAt ? new Date(cuisine.createdAt).toLocaleString() : '-',
      updatedAt: cuisine.updatedAt ? new Date(cuisine.updatedAt).toLocaleString() : '-',
    }));
  };

  const columns = [
    {
      key: 'image',
      label: 'Image',
      render: (value) => value && (
        <img src={value} alt="Cuisine" className="w-12 h-12 object-cover rounded" />
      )
    },
    { key: 'label', label: 'Label' },
    { key: 'status', label: 'Status' },
    { key: 'createdAt', label: 'Created At' },
    { key: 'updatedAt', label: 'Updated At' },
  ];

  const actions = [
    { label: 'Edit', onClick: handleEdit, color: 'blue' },
    { label: 'Delete', onClick: handleDelete, color: 'red' },
  ];

  if (loading && items.length === 0) return (
    <div className="p-4">
      <Loader />
    </div>
  );

  if (error) return (
    <div className="p-4">
      <div className="text-red-500">Error: {error}</div>
    </div>
  );

  return (
    <div className="p-4">
      <h1 className="text-xl font-bold mb-4">Cuisines</h1>
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={handleCreateCuisine}
          className="bg-green-500 text-white px-4 py-2 rounded"
        >
          Create New Cuisine
        </button>
        <div className="text-sm text-gray-600">
          {searchTerm ? `Found ${total} cuisines` : `Showing ${Math.min(displayLimit, items.length)} of ${total} cuisines`}
        </div>
      </div>

      <DndTable
        items={formatTableData(items)}
        columns={columns}
        actions={actions}
        onDragEnd={handleDragEnd}
        onSearch={handleSearch}
        searchDelay={1000}
        searchValue={searchTerm}
        enableRowSelection={true}
        bulkActions={[
          { label: 'Activate Selected', color: 'green', onClick: handleBulkStatusChange(1) },
          { label: 'Deactivate Selected', color: 'gray', onClick: handleBulkStatusChange(0) },
          { label: 'Delete Selected', color: 'red', onClick: handleBulkDelete },
        ]}
      />

      {/* Show load more button only if there are items */}
      {items.length > 0 && hasMore && items.length < total && (
        <div className="mt-6 flex flex-col items-center space-y-2">
          <button
            onClick={handleLoadMore}
            disabled={loading}
            className={`
              inline-flex items-center justify-center
              px-6 py-3 rounded-lg
              text-base font-medium
              transition-all duration-200 ease-in-out
              ${loading 
                ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                : "bg-blue-500 hover:bg-blue-600 text-white hover:shadow-lg transform hover:-translate-y-0.5"
              }
            `}
          >
            {loading ? (
              <>
                <LoadingSpinner className="mr-2" />
                <span>Loading...</span>
              </>
            ) : (
              <>
                <svg 
                  className="w-5 h-5 mr-2" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
                <span>Load More</span>
              </>
            )}
          </button>
          <p className="text-sm text-gray-500">
            {getRemainingItemsCount()} more items to load
            
          </p>
        </div>
      )}

      {/* Show all loaded message only if there are items */}
      {items.length > 0 && !hasMore && (
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            All cuisines loaded
          </p>
        </div>
      )}

      {/* Show no results message only if there are no items */}
      {items.length === 0 && (
        <div className="text-center py-8 bg-gray-50 rounded-lg mt-4">
          <p className="text-gray-500">
            {searchTerm ? `No cuisines found for "${searchTerm}"` : 'No cuisines found'}
          </p>
          <p className="text-gray-400 text-sm mt-2">
            {searchTerm ? 'Try a different search term' : 'Click the Create New button to add one'}
          </p>
        </div>
      )}

      {showDeleteModal && (
        <ConfirmationModal
          message="Are you sure you want to delete this cuisine?"
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
};

export default CuisineList;